// A phone drawn in CSS around a real app screenshot, for the public landing page
// (owner, 5 Nov 2026: the new iPhone and Android screens instead of the old app).
// Everything is a share of the phone's own width or height (percent insets and
// radii on an aspect-ratio box), so one frame looks the same at any size.
//   kind 'ios'      iPhone 16 Pro Max: screen 1320 x 2868, thin bezel, Dynamic Island
//   kind 'android'  Pixel-style: screen 1080 x 2340, a punch-hole camera in the status bar
//   onDark          a lighter metal edge and a faint halo, so a black phone still reads
//                   as a device on the page's night bands
// The screen keeps its size and a light grey before its picture arrives (slow
// networks), and alt text never paints inside the phone.
import React from 'react';
import { Box } from '@mui/material';

// Geometry in shares of the device width (b: bezel, r: outer corner radius);
// the screen keeps the capture's own aspect ratio. Buttons: [side, top, height]
// in shares of the device height, as on the real phones.
const DEVICES = {
  ios: {
    screenAspect: 2868 / 1320, b: 0.032, r: 0.155, body: '#0B0B0C', edge: '#4A4A4F',
    // Action, volume up and down on the left; the side button and Camera Control on the right.
    buttons: [['left', 0.16, 0.04], ['left', 0.23, 0.07], ['left', 0.32, 0.07], ['right', 0.24, 0.11], ['right', 0.6, 0.05]],
  },
  android: {
    screenAspect: 2340 / 1080, b: 0.034, r: 0.12, body: '#16181B', edge: '#3D4146',
    // Power and volume, both on the right.
    buttons: [['right', 0.22, 0.06], ['right', 0.31, 0.11]],
  },
};

const pct = (v) => `${(v * 100).toFixed(3)}%`;

export default function PhoneFrame({ kind = 'ios', src, alt, width, loading = 'lazy', priority = false, sx, shadow = true, onDark = false }) {
  const d = DEVICES[kind] || DEVICES.ios;
  const screenW = 1 - 2 * d.b;                 // of the device width
  const screenH = screenW * d.screenAspect;    // of the device width
  const deviceH = screenH + 2 * d.b;           // of the device width
  const insetY = d.b / deviceH;                // of the device height
  const innerR = d.r - d.b;
  const edge = onDark ? '#8E8A85' : d.edge;
  return (
    <Box
      sx={{
        position: 'relative',
        width,
        aspectRatio: `1 / ${deviceH.toFixed(4)}`,
        borderRadius: `${pct(d.r)} / ${pct(d.r / deviceH)}`,
        bgcolor: d.body,
        // The metal edge, then the drop shadow that lifts the phone off the page.
        boxShadow: [
          `inset 0 0 0 1.5px ${edge}`,
          'inset 0 0 0 3px rgba(0, 0, 0, 0.6)',
          onDark ? '0 0 0 1px rgba(255, 255, 255, 0.10)' : null,
          shadow ? '0 50px 90px -30px rgba(15, 8, 4, 0.55)' : null,
          shadow ? '0 24px 40px -24px rgba(15, 8, 4, 0.45)' : null,
        ].filter(Boolean).join(', '),
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        fetchPriority={priority ? 'high' : undefined}
        draggable={false}
        sx={{
          position: 'absolute',
          left: pct(d.b),
          top: pct(insetY),
          width: pct(screenW),
          height: pct(screenH / deviceH),
          display: 'block',
          objectFit: 'cover',
          borderRadius: `${pct(innerR / screenW)} / ${pct(innerR / screenH)}`,
          bgcolor: '#F2F2F2',
          color: 'transparent',
          userSelect: 'none',
        }}
      />
      {kind === 'ios' ? (
        // Dynamic Island: 126 x 37 pt at 11 pt from the top of a 440-pt screen.
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: pct((d.b + screenW * (11 / 440)) / deviceH),
            width: pct(screenW * (126 / 440)),
            height: pct((screenW * (37 / 440)) / deviceH),
            borderRadius: '999px',
            bgcolor: '#000',
          }}
        />
      ) : (
        // Punch-hole camera, centred on the status bar's row (7.5 % of the screen width down).
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            top: pct((d.b + screenW * 0.075) / deviceH),
            width: pct(screenW * 0.034),
            aspectRatio: '1',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            bgcolor: '#050505',
            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
          }}
        />
      )}
      {/* The buttons, a hair outside the body. */}
      {d.buttons.map(([side, top, height]) => (
        <Box
          key={side + top}
          aria-hidden
          sx={{
            position: 'absolute',
            [side]: '-1.1%',
            top: pct(top),
            width: '1.4%',
            height: pct(height),
            borderRadius: side === 'right' ? '0 3px 3px 0' : '3px 0 0 3px',
            bgcolor: edge,
          }}
        />
      ))}
    </Box>
  );
}
